/**
 * usePresenceOrchestrator — React binding for the pure presenceOrchestrator.
 *
 * App-Feel 2027 §3B / §5.2 seam #1. This is the ONLY way FloatingDjay's
 * proactive triggers (welcome / viewGreet / errorTip / timeEgg / idle) reach
 * the bubble + voice now. Each trigger no longer speaks directly — it calls
 * `requestSpeak(kind)` and the orchestrator decides via the session budget +
 * cadence. Interactive speech (tap/hover/easter-eggs) bypasses this entirely.
 *
 * The hook owns the mutable budget in a ref (no re-render needed for a pure
 * decision) and resets it when the account changes — mirroring the existing
 * `rapidTapRef.resetAll()` reset path so account B never inherits account A's
 * budget. Cleanup of the speak side-effect (bubble + sound) stays with the
 * caller; the hook only governs *whether* it happens.
 */

import { useCallback, useEffect, useRef } from 'react'
import {
  commitSpoke,
  createPresenceState,
  evaluatePresence,
  type PresenceState,
  type PresenceVerdict,
} from './presenceOrchestrator'
import type { ProactiveKind } from './presenceTypes'

interface UsePresenceOrchestratorArgs {
  /** Account id — budget resets when this changes. */
  accountId: string | undefined
  /** True when the current surface already owns a primary Djay voice. */
  surfaceHasOwnDjay: boolean
}

export interface UsePresenceOrchestratorReturn {
  /**
   * Ask the orchestrator for permission to make a proactive utterance NOW.
   * On `allow:true` it has already debited the budget; the caller should then
   * show the bubble + play `mascot.speak`. On `allow:false` the caller stays
   * silent (the verdict's `reason` is available for debugging/telemetry).
   */
  requestSpeak: (kind: ProactiveKind) => PresenceVerdict
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

export function usePresenceOrchestrator({
  accountId,
  surfaceHasOwnDjay,
}: UsePresenceOrchestratorArgs): UsePresenceOrchestratorReturn {
  const stateRef = useRef<PresenceState>(createPresenceState())

  // Reset the session budget on account switch (same lifecycle as the
  // easter-egg/rapid-tap reset in FloatingDjay).
  useEffect(() => {
    stateRef.current = createPresenceState()
  }, [accountId])

  // Keep the latest surface flag in a ref so `requestSpeak` stays referentially
  // stable (callers wire it into effects without re-arming on every change).
  const surfaceRef = useRef(surfaceHasOwnDjay)
  useEffect(() => {
    surfaceRef.current = surfaceHasOwnDjay
  }, [surfaceHasOwnDjay])

  const requestSpeak = useCallback((kind: ProactiveKind): PresenceVerdict => {
    const at = now()
    const verdict = evaluatePresence(
      stateRef.current,
      { kind, at },
      { surfaceHasOwnDjay: surfaceRef.current },
    )
    if (verdict.allow) {
      stateRef.current = commitSpoke(stateRef.current, at)
    }
    return verdict
  }, [])

  return { requestSpeak }
}
